import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AshdaleLogoSVG from '../../assets/ashdale-logo.svg';
import ConnectionStatus from './ConnectionStatus';

const { FiMenu, FiX, FiUser, FiLogOut, FiChevronDown, FiShield, FiSettings } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAdminKey, setShowAdminKey] = useState(false);
  const [adminPhrase, setAdminPhrase] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSecretTrigger = (e) => {
    e.preventDefault();
    setShowAdminKey(true);
  };

  const handleAdminAuth = (e) => {
    e.preventDefault();
    if (adminPhrase === "I am Obi-Wan Kenobi. Last Jedi Master") {
      setShowAdminKey(false);
      setAdminPhrase('');
      // Unlock the admin route for this browser session
      sessionStorage.setItem('adminUnlocked', 'true');
      
      // If user is already logged in as admin, go directly to admin
      if (user?.isAdmin || user?.email === 'carlo.gunter@attitude-dynamics.com') {
        navigate('/admin');
      } else {
        // Otherwise, send them to login but flag that we want admin access
        navigate('/login', { state: { adminMode: true, redirectTo: '/admin' } });
      }
    } else {
      // Clear the input if wrong
      setAdminPhrase('');
    }
  };

  return (
    <header className="bg-black shadow-lg border-b border-primary-500/30 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          
          <Link to="/" className="flex items-center group">
            <img 
              src={AshdaleLogoSVG} 
              alt="Ashdale Analytics" 
              className="h-12 w-auto transition-transform group-hover:scale-105" 
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-10">
            <div className="relative group">
              <button className="text-secondary-300 hover:text-primary-500 flex items-center space-x-1 py-2 text-sm font-semibold transition-colors">
                <span>Calculators</span>
                <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
              </button>
              
              <div className="absolute top-full -left-4 mt-1 w-56 bg-secondary-900 shadow-2xl rounded-xl border border-primary-500/20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden">
                <Link 
                  to="/calculators/retirement" 
                  onContextMenu={handleSecretTrigger}
                  className="block px-6 py-4 text-sm text-white hover:bg-primary-500 hover:text-black transition-colors"
                >
                  Retirement Planning
                </Link>
                <Link to="/calculators/home" className="block px-6 py-4 text-sm text-white hover:bg-primary-500 hover:text-black transition-colors">
                  Home Purchase
                </Link>
                <Link to="/calculators/education" className="block px-6 py-4 text-sm text-white hover:bg-primary-500 hover:text-black transition-colors">
                  Education Planning
                </Link>
              </div>
            </div>

            {user ? (
              <div className="flex items-center space-x-6">
                <Link to="/portfolio" className="text-secondary-300 hover:text-primary-500 text-sm font-semibold transition-colors flex items-center">
                  My Portfolio
                </Link>
                
                {user.isAdmin && (
                  <button 
                    onClick={() => setShowAdminKey(true)}
                    className="text-primary-500 hover:text-primary-400 text-sm font-bold flex items-center px-3 py-1 bg-primary-500/10 rounded-full border border-primary-500/20"
                  >
                    <SafeIcon icon={FiShield} className="w-4 h-4 mr-2" />
                    Control Room
                  </button>
                )}

                <div className="flex items-center space-x-3 pl-6 border-l border-secondary-800">
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="w-4 h-4 text-primary-500" />
                  </div>
                  <span className="text-sm font-medium text-white">{user.name}</span>
                  <button 
                    onClick={handleLogout}
                    className="text-secondary-400 hover:text-red-500 transition-colors p-1"
                  >
                    <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-6">
                <Link to="/login" className="text-secondary-300 hover:text-primary-500 text-sm font-semibold transition-colors">
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  className="bg-primary-500 hover:bg-primary-400 text-black px-6 py-2.5 rounded-lg text-sm font-bold transition-all shadow-lg shadow-primary-500/20 active:scale-95"
                >
                  Get Started
                </Link>
              </div>
            )}
            
            <ConnectionStatus />
          </nav>

          <div className="md:hidden flex items-center space-x-4">
            <ConnectionStatus />
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-white"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-7 h-7" />
            </button>
          </div>
        </div>
      </div>

      {/* Secret Admin Phrase Modal */}
      <AnimatePresence>
        {showAdminKey && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-secondary-900 border border-primary-500/30 p-8 rounded-2xl max-w-md w-full shadow-2xl"
            >
              <h3 className="text-xl font-bold text-white mb-4 text-center">Identity Verification Required</h3>
              <form onSubmit={handleAdminAuth}>
                <label className="block text-sm text-secondary-400 mb-2">Speak the hidden phrase:</label>
                <input 
                  type="text"
                  autoFocus
                  value={adminPhrase}
                  onChange={(e) => setAdminPhrase(e.target.value)}
                  className="w-full bg-black border border-secondary-700 rounded-lg px-4 py-3 text-white mb-6 focus:ring-2 focus:ring-primary-500 outline-none"
                  placeholder="..."
                />
                <div className="flex space-x-3">
                  <button 
                    type="submit"
                    className="flex-1 bg-primary-500 text-black font-bold py-2 rounded-lg hover:bg-primary-400 transition-colors"
                  >
                    Enter Control Room
                  </button>
                  <button 
                    type="button"
                    onClick={() => setShowAdminKey(false)}
                    className="flex-1 bg-secondary-800 text-white font-bold py-2 rounded-lg hover:bg-secondary-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;